import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts'
import {
  useHealthChecks,
  useCheckHistory,
  type HealthCheck,
} from '@/lib/health-checks'
import { HealthCheckModal } from './HealthCheckModal'
import { HealthCheckCard } from './HealthCheckCard'

interface Props { serviceId: string }

function HistorySparkline({ id }: { id: string }) {
  const { data } = useCheckHistory(id, '24h')
  const points = (data?.buckets ?? []).map((b, i) => ({
    i,
    uptime: b.uptime_pct,
  }))
  return (
    <div className="h-12">
      <ResponsiveContainer>
        <LineChart data={points}>
          <XAxis dataKey="i" hide />
          <YAxis hide domain={[0, 100]} />
          <Tooltip formatter={(v) => `${(v as number).toFixed(1)}%`} />
          <Line dataKey="uptime" stroke="currentColor" dot={false} strokeWidth={1.5} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export function HealthCheckPanel({ serviceId }: Props) {
  const { data: checks } = useHealthChecks(serviceId)
  const [editing, setEditing] = useState<HealthCheck | null>(null)
  const [creating, setCreating] = useState(false)

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">Health checks</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          + Add
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {(checks?.length ?? 0) === 0 && (
          <p className="text-xs text-muted-foreground">
            No health checks configured for this service.
          </p>
        )}
        {checks?.map((c) => (
          <div key={c.id} className="space-y-1">
            <HealthCheckCard check={c} onEdit={setEditing} />
            <HistorySparkline id={c.id} />
          </div>
        ))}
      </CardContent>

      {/* The modal seeds its form from `initial` in useState initializers, which
          only run on mount. Without a changing key the instance mounted here on
          first render (initial=undefined) keeps its create-defaults, and saving
          an edit would write those defaults over the stored check. */}
      <HealthCheckModal
        key={editing?.id ?? 'new'}
        open={creating || !!editing}
        initial={editing ?? undefined}
        onClose={() => { setCreating(false); setEditing(null) }}
      />
    </Card>
  )
}
